import { Component, ElementRef, Input, OnChanges, ViewChild, ViewEncapsulation } from '@angular/core';
import { ComponentData } from '@ddt-web/data-service';
import { ResponsiveDisplayConstants } from '@ddt-web/dom';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import * as get from 'lodash/get';
import { Subject, timer } from 'rxjs';
import { debounce } from 'rxjs/operators';

import { AreaSplineChartConfig, BarChartConfig, ChartType, ColumnChartConfig, DoughnutChartConfig, EmptyChartColumnConfig, GridChartConfig, HeatmapChartConfig, IndicatorColumnChartConfig, LineChartConfig, PieChartConfig, ProgressBarConfig, ScatterChartConfig } from './config';

@UntilDestroy()
@Component({
	selector: 'bfa-chart',
	templateUrl: './chart.component.html',
	styleUrls: ['./chart.component.scss'],
	encapsulation: ViewEncapsulation.None
})
export class ChartComponent implements OnChanges {
	@Input() public colors?: Array<string>;
	@Input() public data: any;
	@Input('data-index') public dataIndex?: number;
	@Input() public hasFullHeight: boolean = false;
	@Input() public height?: number;
	@Input() public width?: number;
	@Input() public name: string;
	@Input() public type: ChartType;
	@Input() public title?: string;
	@Input('hide-title') public hideTitle: boolean = false;
	@Input() public x?: string;
	@Input() public y?: string;
	@Input() public tooltipType?: string;
	@Input() public customAttributes?: any;
	@Input() public opposite: boolean = false;
	@Input() public refreshEvent$: Subject<boolean>;

	@ViewChild('chartContainer', { static: true }) public container: ElementRef;
	public batch: number = 0;

	constructor(private elementRef: ElementRef) { }

	public ngOnChanges(): void {
		if (this.data) {
			if (this.data instanceof ComponentData) {
				this.data = get(this.data, 'tabs[0]');
			}
			this.initializeChart();
			// if it's listening to refreshEvents, no need to have a window resize
			if (!this.refreshEvent$) {
				// Adding a window resize event as Highcharts 'reflow' doesn't work inside flexboxes.
				window.addEventListener('resize', () => {
					this.initializeChart();
				});
			} else {
				// Listen to any refreshEvents
				this.refreshEvent$.pipe(untilDestroyed(this), debounce(() => timer(50))).subscribe(event => {
					this.initializeChart();
				});
			}

		}
	}

	private initializeChart(): void {
		setTimeout(() => {
			if (this.hasFullHeight && window.innerWidth >= ResponsiveDisplayConstants.DEVICE_WIDTH.MOBILE_MAX) {
				this.height = this.elementRef.nativeElement.offsetHeight;
			}
			let args = [
				null,
				this.type,
				this.name,
				this.container.nativeElement,
				this.data,
				this.dataIndex,
				this.colors,
				this.height,
				this.width,
				this.title,
				this.hideTitle,
				this.x,
				this.y,
				this.customAttributes,
				this.tooltipType,
				this.opposite
			];
			const chartTypeConfigs = {
				bar: BarChartConfig,
				column: ColumnChartConfig,
				pie: PieChartConfig,
				grid: GridChartConfig,
				scatter: ScatterChartConfig,
				emptyColumnChart: EmptyChartColumnConfig,
				indicatorColumnChart: IndicatorColumnChartConfig,
				line: LineChartConfig,
				doughnutChart: DoughnutChartConfig,
				heatmap: HeatmapChartConfig,
				areaspline: AreaSplineChartConfig,
				progressBar: ProgressBarConfig
			};

			// add the batch arg for pie charts
			if (this.type === ChartType.Pie) {
				args = [
					null,
					this.type,
					this.name,
					this.container.nativeElement,
					this.data,
					this.dataIndex,
					this.batch,
					this.colors,
					this.height,
					this.width,
					this.title,
					this.hideTitle,
					this.x,
					this.y,
					this.customAttributes,
					this.tooltipType
				];
			}

			const chart = new (Function.prototype.bind.apply(chartTypeConfigs[this.type], args))();

			chart.render();
		}, 100);
	}
}
